/*
  ЦИФРОВОЙ ДИАК ЗФ — REPORTS PAGE (executive-grade)

  Управленческий отчёт для руководства. Принципы:
   • BLUF — сверху повествовательная ИИ-сводка + статус-светофор (🟢/🟡/🔴).
   • Немного осмысленных KPI с полными подписями и контекстом (было→стало).
   • «Требует внимания» — отдельным заметным блоком.
   • Прогрессивное раскрытие: детали (списки задач) свёрнуты, разворачиваются
     по клику, названия задач показываются ПОЛНОСТЬЮ (без обрезки «…»).

  Данные: /api/reports/biweekly (структура, быстро) + /api/reports/narrative
  (ИИ-сводка из реальных цифр, асинхронно).
*/

import { useState, useEffect, useCallback, useMemo } from "react";
import Sidebar from "@/components/Sidebar";
import { apiJson } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  BarChart3,
  CheckCircle2,
  Clock,
  AlertTriangle,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Megaphone,
  Loader2,
  ChevronDown,
  Printer,
  Sparkles,
  Flag,
  Trash2,
  Save,
  Archive,
  ArrowLeft,
  FileText,
} from "lucide-react";

/* ───────────────────── Types (контракт с бэком) ───────────────────── */
interface CardRef {
  projectId: string;
  projectTitle: string;
  cardId: string;
  title: string;
  assignee?: string;
  priority?: string;
  dueDate?: string | null;
  status?: string;
  side?: "customer" | "office";
  escalation?: string;
}
interface PersonSummary {
  assignee: string;
  completedInPeriod: CardRef[];
  inProgressNow: CardRef[];
  newInPeriod: CardRef[];
  overdueNow: CardRef[];
  workloadActive: number;
}
interface ProjectProgressRow {
  projectId: string; projectTitle: string; phaseLabel?: string; group?: string;
  progressBefore: number; progressAfter: number; progressDelta: number;
  backlogTotal: number; doneTotal: number; inProgressTotal: number; overdueTotal: number;
}
interface PhaseProgress {
  phaseId: string; phaseLabel: string; projectsCount: number;
  progressBefore: number; progressAfter: number; progressDelta: number;
}
interface ShiftedGanttTask {
  projectId: string; projectTitle: string; taskId: string; title: string;
  oldEnd?: string; newEnd?: string;
}
interface RiskRef {
  projectId: string; projectTitle: string; riskId: string; title: string;
  status?: string; impact?: string; probability?: string;
}
interface SideSummary {
  side: "customer" | "office";
  completedInPeriod: CardRef[]; inProgressNow: CardRef[]; blockers: CardRef[];
}
type Health = "green" | "amber" | "red" | "notstarted";
interface PortfolioProject {
  projectId: string; projectTitle: string; phaseLabel?: string;
  health: Health; healthReason: string;
  expectedProgress: number | null; scheduleVariance: number | null;
  progressBefore: number; progressAfter: number; progressDelta: number;
  doneTotal: number; inProgressTotal: number; overdueTotal: number; backlogTotal: number;
  completedInPeriod: CardRef[]; inProgressNow: CardRef[]; overdue: CardRef[]; risks: RiskRef[];
}
interface BiweeklyReport {
  period: { from: string; to: string; generatedAt: string };
  summary: {
    totalCards: number; doneTotal: number; inProgressTotal: number; overdueTotal: number;
    completedInPeriod: number; newInPeriod: number;
    averageProgressBefore: number; averageProgressAfter: number;
  };
  movements: {
    completed: CardRef[]; started: CardRef[]; added: CardRef[];
    overdue: CardRef[]; shiftedGantt: ShiftedGanttTask[];
  };
  byProject: ProjectProgressRow[];
  portfolio: PortfolioProject[];
  byPerson: PersonSummary[];
  byPhase: PhaseProgress[];
  bySide: { customer: SideSummary; office: SideSummary };
  risks: { current: RiskRef[]; newInPeriod: RiskRef[] };
  escalations: CardRef[];
}
interface Narrative {
  headline: string;
  status: "green" | "amber" | "red";
  statusReason: string;
  summary: string;
  attention: string[];
}
/** Лёгкое представление «точки отчёта» (без тяжёлого payload). */
interface LightPoint {
  id: string;
  capturedAt: string;
  label: string;
  note: string;
  authorName: string | null;
  projectsCount: number;
  averageProgress: number;
}
/** Сохранённый отчёт — карточка для архива (лёгкая). */
interface LightSaved {
  id: string;
  savedAt: string;
  title: string;
  authorName: string | null;
  fromLabel: string;
  fromDate: string;
  toLabel: string;
  toDate: string;
  status: "green" | "amber" | "red" | null;
  hasNarrative: boolean;
}
/** Сохранённый отчёт целиком (заморожённые цифры + ИИ-сводка). */
interface SavedReportFull {
  id: string;
  savedAt: string;
  title: string;
  authorName: string | null;
  fromPointId: string | null;
  toPointId: string | null;
  fromLabel: string;
  fromDate: string;
  toLabel: string;
  toDate: string;
  report: BiweeklyReport;
  narrative: Narrative | null;
  answers: Array<{ question: string; answer: string }>;
}

/* ───────────────────── helpers ───────────────────── */
/** Русская плюрализация: 1 день, 2 дня, 5 дней. */
function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) { const d = new Date(iso); return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "numeric" }); }
  return new Date(`${m[1]}-${m[2]}-${m[3]}`).toLocaleDateString("ru-RU", { day: "2-digit", month: "long", year: "numeric" });
}
function fmtDateShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return new Date(`${m[1]}-${m[2]}-${m[3]}`).toLocaleDateString("ru-RU", { day: "2-digit", month: "short" });
}
function priorityClass(p?: string): string {
  if (p === "critical") return "bg-red-100 text-red-700";
  if (p === "high") return "bg-orange-100 text-orange-700";
  if (p === "medium") return "bg-amber-50 text-amber-700";
  return "bg-muted/50 text-muted-foreground";
}
function priorityLabel(p?: string): string {
  return p === "critical" ? "Критич." : p === "high" ? "Высокий" : p === "medium" ? "Средний" : "Низкий";
}

const STATUS_META = {
  green: { label: "В норме", color: "#00A651", bg: "rgba(0,166,81,0.08)", ring: "rgba(0,166,81,0.4)" },
  amber: { label: "Требует внимания", color: "#E8A300", bg: "rgba(232,163,0,0.08)", ring: "rgba(232,163,0,0.4)" },
  red: { label: "Критично", color: "#E11D48", bg: "rgba(225,29,72,0.08)", ring: "rgba(225,29,72,0.4)" },
} as const;

const HEALTH_META: Record<Health, { label: string; color: string }> = {
  green: { label: "По графику", color: "#00A651" },
  amber: { label: "Отставание", color: "#E8A300" },
  red: { label: "Срыв сроков", color: "#E11D48" },
  notstarted: { label: "Не начат", color: "#94A3B8" },
};

/* ───────────────────── small components ───────────────────── */
function RagDot({ health }: { health: Health }) {
  return <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: HEALTH_META[health].color }} />;
}

function Delta({ value }: { value: number }) {
  return (
    <span className={cn(
      "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold",
      value > 0 && "bg-emerald-50 text-emerald-700",
      value < 0 && "bg-red-50 text-red-700",
      value === 0 && "bg-muted/50 text-muted-foreground",
    )}>
      {value > 0 ? <ArrowUpRight size={11} /> : value < 0 ? <ArrowDownRight size={11} /> : null}
      {value > 0 ? `+${value}` : value}
    </span>
  );
}

function Kpi({ icon, label, value, sub, tone }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; sub?: React.ReactNode;
  tone?: "default" | "danger" | "ok";
}) {
  return (
    <div className={cn(
      "rounded-xl border bg-card p-4 shadow-sm",
      tone === "danger" ? "border-red-200" : "border-border",
    )}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <span className={cn(
          "flex h-7 w-7 items-center justify-center rounded-lg",
          tone === "danger" ? "bg-red-50 text-red-600" : tone === "ok" ? "bg-emerald-50 text-emerald-600" : "bg-[var(--nn-blue-pale)]/60 text-[var(--nn-blue)]",
        )}>{icon}</span>
        <span className="text-[12px] font-medium leading-tight">{label}</span>
      </div>
      <div className="mt-2 text-3xl font-bold leading-none" style={{ color: tone === "danger" ? "#E11D48" : "var(--nn-dark)" }}>
        {value}
      </div>
      {sub ? <div className="mt-1.5 text-[12px] text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

/** Свёрнутый блок с деталями. Нативный <details> — надёжно, без JS-состояния. */
function Collapsible({ title, count, accent, defaultOpen, children }: {
  title: string; count?: number; accent?: string; defaultOpen?: boolean; children: React.ReactNode;
}) {
  return (
    <details open={defaultOpen} className="group rounded-xl border border-border bg-card shadow-sm">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 [&::-webkit-details-marker]:hidden">
        <ChevronDown size={16} className="shrink-0 text-muted-foreground transition-transform group-open:rotate-0 -rotate-90" />
        <span className="text-sm font-bold" style={{ color: "var(--nn-dark)" }}>{title}</span>
        {count !== undefined ? (
          <span
            className="ml-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
            style={{ background: accent ? `${accent}1a` : "var(--nn-blue-pale)", color: accent ?? "var(--nn-blue)" }}
          >
            {count}
          </span>
        ) : null}
      </summary>
      <div className="border-t border-border/60 px-4 py-3">{children}</div>
    </details>
  );
}

/** Карточка проекта в портфеле: RAG-статус + прогресс + drill-down по этому проекту. */
function PortfolioCard({ p }: { p: PortfolioProject }) {
  const meta = HEALTH_META[p.health];
  const hasDetail = p.overdue.length + p.inProgressNow.length + p.completedInPeriod.length + p.risks.length > 0;
  return (
    <details className="group rounded-xl border bg-background/40 shadow-sm" style={{ borderColor: `${meta.color}40` }}>
      <summary className="flex cursor-pointer list-none items-start gap-3 p-3 [&::-webkit-details-marker]:hidden">
        <span className="mt-1 flex flex-col items-center gap-1">
          <RagDot health={p.health} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <span className="min-w-0 break-words text-sm font-semibold leading-snug" style={{ color: "var(--nn-dark)" }}>
              {p.projectTitle}
            </span>
            <span className="shrink-0 text-xs font-semibold tabular-nums" style={{ color: meta.color }}>
              {p.progressAfter}%
            </span>
          </div>
          {p.phaseLabel ? <div className="text-[11px] text-muted-foreground">{p.phaseLabel}</div> : null}
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted/60">
            <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, p.progressAfter))}%`, background: meta.color }} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
            <span style={{ color: meta.color }} className="font-medium">{meta.label}: {p.healthReason}</span>
          </div>
          {p.expectedProgress !== null ? (
            <div className="mt-1 text-[11px] text-muted-foreground">
              план на сегодня: {p.expectedProgress}%
              {p.scheduleVariance !== null ? (
                <span className={cn("ml-1 font-medium", p.scheduleVariance >= 0 ? "text-emerald-700" : "text-red-700")}>
                  ({p.scheduleVariance >= 0 ? `+${p.scheduleVariance}` : p.scheduleVariance} п.п. {p.scheduleVariance >= 0 ? "опережение" : "отставание"})
                </span>
              ) : null}
            </div>
          ) : null}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
            <span className="text-muted-foreground">было {p.progressBefore}%</span>
            <Delta value={p.progressDelta} />
            <span className="text-emerald-700">завершено за период: {p.completedInPeriod.length}</span>
            <span className="text-[var(--nn-blue)]">в работе: {p.inProgressTotal}</span>
            {p.overdueTotal > 0 ? <span className="font-semibold text-red-700">просрочено: {p.overdueTotal}</span> : null}
            {p.risks.length > 0 ? <span className="text-amber-700">рисков: {p.risks.length}</span> : null}
          </div>
        </div>
        {hasDetail ? <ChevronDown size={16} className="mt-1 shrink-0 text-muted-foreground transition-transform -rotate-90 group-open:rotate-0" /> : null}
      </summary>
      {hasDetail ? (
        <div className="space-y-3 border-t border-border/50 px-3 py-3">
          {p.overdue.length > 0 ? (
            <div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-red-700">Просрочено ({p.overdue.length})</div>
              <TaskList items={p.overdue} empty="—" />
            </div>
          ) : null}
          {p.inProgressNow.length > 0 ? (
            <div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--nn-blue)]">В работе ({p.inProgressNow.length})</div>
              <TaskList items={p.inProgressNow} empty="—" />
            </div>
          ) : null}
          {p.completedInPeriod.length > 0 ? (
            <div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Завершено за период ({p.completedInPeriod.length})</div>
              <TaskList items={p.completedInPeriod} empty="—" />
            </div>
          ) : null}
          {p.risks.length > 0 ? (
            <div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">Риски ({p.risks.length})</div>
              <ul className="space-y-1">
                {p.risks.map((r) => (
                  <li key={r.riskId} className="rounded border border-border/50 bg-card px-2 py-1.5 text-sm">
                    <span className="break-words">{r.title}</span>
                    {r.impact ? <span className="ml-1 rounded bg-muted/50 px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">влияние: {r.impact}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </details>
  );
}

/** Список задач — ПОЛНЫЕ названия (перенос, без обрезки). */
function TaskList({ items, empty }: { items: CardRef[]; empty: string }) {
  if (items.length === 0) return <p className="text-sm italic text-muted-foreground">{empty}</p>;
  return (
    <ul className="space-y-1.5">
      {items.map((it) => (
        <li key={`${it.projectId}/${it.cardId}`} className="flex items-start gap-2 rounded-lg border border-border/50 bg-background/50 px-2.5 py-2 text-sm">
          <span className={cn("mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold", priorityClass(it.priority))}>
            {priorityLabel(it.priority)}
          </span>
          <span className="min-w-0 flex-1 break-words leading-snug">{it.title}</span>
          <span className="ml-1 shrink-0 text-right text-[11px] text-muted-foreground">
            <span className="block">{it.projectTitle}</span>
            <span className="block">
              {it.assignee ? `${it.assignee}` : ""}{it.assignee && it.dueDate ? " · " : ""}{it.dueDate ? `до ${fmtDateShort(it.dueDate)}` : ""}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ───────────────────── page ───────────────────── */
export default function ReportsPage() {
  const [points, setPoints] = useState<LightPoint[]>([]);
  const [pointsLoaded, setPointsLoaded] = useState(false);
  const [mode, setMode] = useState<"prev" | "baseline">("prev");
  const [fromPointId, setFromPointId] = useState<string | null>(null);
  const [toPointId, setToPointId] = useState<string | null>(null);
  const [report, setReport] = useState<BiweeklyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedList, setSavedList] = useState<LightSaved[]>([]);
  const [viewingSaved, setViewingSaved] = useState<SavedReportFull | null>(null);

  const loadPoints = useCallback(async (): Promise<LightPoint[]> => {
    const res = await apiJson<{ ok: true; points: LightPoint[] }>("/api/reports/points");
    const sorted = [...res.points].sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
    setPoints(sorted);
    setPointsLoaded(true);
    return sorted;
  }, []);

  /** Выбор «от/до» по режиму: prev = предпоследняя→последняя; baseline = старт→последняя. */
  const applySelection = useCallback((pts: LightPoint[], m: "prev" | "baseline") => {
    if (pts.length < 2) {
      setFromPointId(null);
      setToPointId(pts[pts.length - 1]?.id ?? null);
      return;
    }
    setToPointId(pts[pts.length - 1].id);
    setFromPointId((m === "baseline" ? pts[0] : pts[pts.length - 2]).id);
  }, []);

  useEffect(() => {
    loadPoints()
      .then((pts) => applySelection(pts, "prev"))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [loadPoints, applySelection]);

  const fetchReport = useCallback(async () => {
    if (!fromPointId || !toPointId) {
      setReport(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiJson<{ ok: true; report: BiweeklyReport }>(
        `/api/reports/biweekly?fromPoint=${encodeURIComponent(fromPointId)}&toPoint=${encodeURIComponent(toPointId)}`,
      );
      setReport(res.report);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось собрать отчёт");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [fromPointId, toPointId]);

  useEffect(() => { void fetchReport(); }, [fetchReport]);

  const capturePoint = useCallback(async () => {
    setCapturing(true);
    setError(null);
    try {
      await apiJson("/api/reports/points", { method: "POST", body: JSON.stringify({}) });
      const pts = await loadPoints();
      applySelection(pts, mode);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось зафиксировать точку");
    } finally {
      setCapturing(false);
    }
  }, [loadPoints, applySelection, mode]);

  const deletePoint = useCallback(
    async (id: string) => {
      if (!window.confirm("Удалить эту точку отчёта? Сравнения по ней больше не будет.")) return;
      setError(null);
      try {
        await apiJson(`/api/reports/points/${encodeURIComponent(id)}`, {
          method: "DELETE",
          body: JSON.stringify({}),
        });
        const pts = await loadPoints();
        applySelection(pts, mode);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Не удалось удалить точку");
      }
    },
    [loadPoints, applySelection, mode],
  );

  const chooseMode = useCallback(
    (m: "prev" | "baseline") => {
      setMode(m);
      applySelection(points, m);
    },
    [points, applySelection],
  );

  const loadSaved = useCallback(async () => {
    try {
      const res = await apiJson<{ ok: true; saved: LightSaved[] }>("/api/reports/saved");
      setSavedList(res.saved);
    } catch {
      /* архив не критичен — не шумим ошибкой */
    }
  }, []);

  useEffect(() => { void loadSaved(); }, [loadSaved]);

  const openSaved = useCallback(async (id: string) => {
    setError(null);
    try {
      const res = await apiJson<{ ok: true; saved: SavedReportFull }>(
        `/api/reports/saved/${encodeURIComponent(id)}`,
      );
      setViewingSaved(res.saved);
      setReport(res.saved.report);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось открыть отчёт");
    }
  }, []);

  const closeSaved = useCallback(() => {
    setViewingSaved(null);
    void fetchReport();
  }, [fetchReport]);

  const deleteSaved = useCallback(
    async (id: string) => {
      if (!window.confirm("Удалить сохранённый отчёт из архива?")) return;
      setError(null);
      try {
        await apiJson(`/api/reports/saved/${encodeURIComponent(id)}`, {
          method: "DELETE",
          body: JSON.stringify({}),
        });
        if (viewingSaved?.id === id) {
          setViewingSaved(null);
          void fetchReport();
        }
        await loadSaved();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Не удалось удалить отчёт");
      }
    },
    [loadSaved, viewingSaved, fetchReport],
  );

  // Клик по точке → открыть её отчёт: динамика «предыдущая → эта». Для baseline
  // (нет предыдущей) показываем «Старт → последняя».
  const openPointReport = useCallback(
    (id: string) => {
      if (points.length < 2) return;
      const idx = points.findIndex((p) => p.id === id);
      if (idx <= 0) {
        setMode("baseline");
        setFromPointId(points[0].id);
        setToPointId(points[points.length - 1].id);
      } else {
        setMode("prev");
        setFromPointId(points[idx - 1].id);
        setToPointId(id);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [points],
  );

  const fromPoint = points.find((p) => p.id === fromPointId) ?? null;
  const toPoint = points.find((p) => p.id === toPointId) ?? null;
  const intervalDays =
    fromPoint && toPoint
      ? Math.max(1, Math.round((Date.parse(toPoint.capturedAt) - Date.parse(fromPoint.capturedAt)) / 864e5))
      : null;
  const s = report?.summary;

  return (
    <div className="flex min-h-screen" style={{ background: "var(--background)" }}>
      <Sidebar />
      <main className="relative min-h-screen flex-1 transition-[margin-left] duration-200 ease-out [margin-left:var(--app-sidebar-width)]">
        {/* Header */}
        <header className="border-b border-border bg-white px-6 py-4 print:hidden">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold" style={{ color: "var(--nn-dark)" }}>Управленческий отчёт</h1>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {viewingSaved
                  ? `Сохранённый отчёт · ${viewingSaved.title}`
                  : "Программа «Цифровой ДИАК ЗФ» · динамика между зафиксированными точками отчёта."}
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              {viewingSaved ? (
                <button type="button" onClick={closeSaved}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/50">
                  <ArrowLeft size={14} /> Назад к текущему
                </button>
              ) : (
                <>
                  {points.length >= 2 ? (
                    <>
                      <div className="inline-flex overflow-hidden rounded-md border border-border text-sm">
                        {(["prev", "baseline"] as const).map((m) => (
                          <button key={m} type="button" onClick={() => chooseMode(m)}
                            className={cn(
                              "px-2.5 py-1.5",
                              mode === m ? "bg-[var(--nn-blue)] text-white" : "bg-background text-foreground/80 hover:bg-muted/50",
                            )}>
                            {m === "prev" ? "Предыдущая → последняя" : "Старт → последняя"}
                          </button>
                        ))}
                      </div>
                      <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <span>от</span>
                        <select value={fromPointId ?? ""}
                          onChange={(e) => { setMode("prev"); setFromPointId(e.target.value); }}
                          className="rounded-md border border-border bg-background px-2 py-1 text-sm">
                          {points.map((p) => (
                            <option key={p.id} value={p.id} disabled={Boolean(toPoint) && p.capturedAt >= (toPoint?.capturedAt ?? "")}>
                              {p.label} · {fmtDateShort(p.capturedAt)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <span>до</span>
                        <select value={toPointId ?? ""}
                          onChange={(e) => { setMode("prev"); setToPointId(e.target.value); }}
                          className="rounded-md border border-border bg-background px-2 py-1 text-sm">
                          {points.map((p) => (
                            <option key={p.id} value={p.id} disabled={Boolean(fromPoint) && p.capturedAt <= (fromPoint?.capturedAt ?? "")}>
                              {p.label} · {fmtDateShort(p.capturedAt)}
                            </option>
                          ))}
                        </select>
                      </label>
                    </>
                  ) : null}
                  <button type="button" onClick={() => void capturePoint()} disabled={capturing}
                    className="inline-flex items-center gap-1.5 rounded-md bg-[var(--nn-blue)] px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-[var(--nn-blue-dark)] disabled:opacity-60">
                    {capturing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flag size={14} />}
                    {capturing ? "Фиксирую…" : "Зафиксировать точку"}
                  </button>
                  {points.length >= 2 ? (
                    <button type="button" onClick={() => void fetchReport()} disabled={loading}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-muted/50 disabled:opacity-60">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw size={14} />} Пересобрать
                    </button>
                  ) : null}
                </>
              )}
              <button type="button" onClick={() => window.print()}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-muted/50">
                <Printer size={14} /> Печать
              </button>
            </div>
          </div>
          {viewingSaved ? (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Период: <span className="font-medium text-foreground/80">{viewingSaved.fromLabel} ({fmtDate(viewingSaved.fromDate)})</span> → <span className="font-medium text-foreground/80">{viewingSaved.toLabel} ({fmtDate(viewingSaved.toDate)})</span> · зафиксирован {fmtDate(viewingSaved.savedAt)}
            </p>
          ) : report && fromPoint && toPoint ? (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Период: <span className="font-medium text-foreground/80">{fromPoint.label} ({fmtDate(fromPoint.capturedAt)})</span> → <span className="font-medium text-foreground/80">{toPoint.label} ({fmtDate(toPoint.capturedAt)})</span>
              {intervalDays ? <span> · прошло {intervalDays} {plural(intervalDays, "день", "дня", "дней")}</span> : null}
            </p>
          ) : null}
        </header>

        {!viewingSaved ? (
          <>
            <ReportPointsPanel
              points={points}
              fromPointId={fromPointId}
              toPointId={toPointId}
              onOpen={openPointReport}
              onDelete={deletePoint}
            />
            <ReportArchivePanel saved={savedList} onOpen={(id) => void openSaved(id)} onDelete={deleteSaved} />
          </>
        ) : null}

        {error ? <div className="m-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div> : null}

        {!viewingSaved && pointsLoaded && points.length === 0 ? (
          <EmptyHint
            title="Ещё нет ни одной точки отчёта"
            text="Зафиксируйте стартовую точку (baseline) — «снимок» статуса программы на сегодня. Спустя ~2 недели зафиксируете следующую, и здесь появится динамика."
            cta="Зафиксировать стартовую точку"
            onCapture={() => void capturePoint()}
            capturing={capturing}
          />
        ) : !viewingSaved && pointsLoaded && points.length === 1 ? (
          <EmptyHint
            title="Есть только стартовая точка"
            text={`Точка «${points[0].label}» зафиксирована ${fmtDate(points[0].capturedAt)}. Чтобы увидеть динамику, зафиксируйте вторую точку — рекомендуем через ~2 недели (каденция гибкая).`}
            cta="Зафиксировать вторую точку"
            onCapture={() => void capturePoint()}
            capturing={capturing}
          />
        ) : null}

        {points.length >= 2 && !report && !error ? (
          <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Собираю отчёт…
          </div>
        ) : null}

        {report && s ? (
          <div className="space-y-5 p-6">
            {/* ─── BLUF: статус + ИИ-сводка (живая или сохранённая) ─── */}
            {viewingSaved ? (
              viewingSaved.narrative ? (
                <section
                  className="overflow-hidden rounded-xl border shadow-sm"
                  style={{ borderColor: (STATUS_META[viewingSaved.narrative.status] ?? STATUS_META.amber).ring }}
                >
                  <div
                    className="p-5"
                    style={{ background: (STATUS_META[viewingSaved.narrative.status] ?? STATUS_META.amber).bg }}
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <Sparkles size={14} className="text-[var(--nn-blue)]" />
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Сводка для руководства · сохранено
                      </span>
                    </div>
                    <NarrativeCard narrative={viewingSaved.narrative} />
                  </div>
                </section>
              ) : (
                <div className="rounded-xl border border-border bg-card/40 p-4 text-sm text-muted-foreground">
                  В этом сохранённом отчёте ИИ-сводки нет — ниже только цифры на момент фиксации.
                </div>
              )
            ) : (
              <ExecSummary
                key={`${fromPointId}_${toPointId}`}
                fromPointId={fromPointId}
                toPointId={toPointId}
                onSaved={() => void loadSaved()}
              />
            )}

            {/* ─── KPI ─── */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Kpi icon={<BarChart3 size={16} />} label="Прогресс программы"
                value={<>{s.averageProgressAfter}%</>}
                sub={<span className="inline-flex items-center gap-1.5">было {s.averageProgressBefore}% <Delta value={s.averageProgressAfter - s.averageProgressBefore} /></span>} />
              <Kpi icon={<CheckCircle2 size={16} />} label="Завершено задач за период" tone="ok"
                value={s.completedInPeriod} sub={`Всего выполнено: ${s.doneTotal} из ${s.totalCards}`} />
              <Kpi icon={<Clock size={16} />} label="Сейчас в работе"
                value={s.inProgressTotal} sub={`Новых за период: ${s.newInPeriod}`} />
              <Kpi icon={<AlertTriangle size={16} />} label="Просрочено — требуют внимания"
                value={s.overdueTotal} tone={s.overdueTotal > 0 ? "danger" : "default"}
                sub={report.escalations.length > 0 ? `Эскалаций руководству: ${report.escalations.length}` : "Эскалаций нет"} />
            </div>

            {/* ─── Дорожная карта (упрощённая) ─── */}
            {report.byPhase.length > 0 ? (
              <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
                <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide" style={{ color: "var(--nn-dark)" }}>
                  <BarChart3 size={15} className="text-[var(--nn-blue)]" /> Дорожная карта программы
                </h2>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {report.byPhase.map((ph, i) => (
                    <div key={ph.phaseId} className="relative rounded-lg border border-border/60 bg-background/40 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--nn-blue)] text-[10px] font-bold text-white">{i + 1}</span>
                        <span className="min-w-0 flex-1 truncate text-sm font-semibold">{ph.phaseLabel}</span>
                      </div>
                      <div className="mb-1 flex items-baseline justify-between text-xs text-muted-foreground tabular-nums">
                        <span>{ph.progressBefore}% → <span className="font-bold text-foreground">{ph.progressAfter}%</span></span>
                        <Delta value={ph.progressDelta} />
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-muted/60">
                        <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, ph.progressAfter))}%`, background: "var(--nn-blue)" }} />
                      </div>
                      <div className="mt-1 text-[10px] text-muted-foreground">Проектов: {ph.projectsCount}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* ─── ПОРТФЕЛЬ ПРОЕКТОВ (центр отчёта, по статусу) ─── */}
            <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide" style={{ color: "var(--nn-dark)" }}>
                  <BarChart3 size={15} className="text-[var(--nn-blue)]" /> Проекты программы — статус
                </h2>
                <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><RagDot health="red" /> Срыв сроков</span>
                  <span className="inline-flex items-center gap-1"><RagDot health="amber" /> Отставание</span>
                  <span className="inline-flex items-center gap-1"><RagDot health="green" /> По графику</span>
                  <span className="inline-flex items-center gap-1"><RagDot health="notstarted" /> Не начат</span>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                {report.portfolio.map((p) => <PortfolioCard key={p.projectId} p={p} />)}
              </div>
            </section>

            {/* ─── Вторичные детали (свёрнуто) ─── */}
            <Collapsible title="Сдвиги плана-графика" count={report.movements.shiftedGantt.length} accent="#E8A300">
              {report.movements.shiftedGantt.length === 0 ? <p className="text-sm italic text-muted-foreground">Сдвигов нет.</p> : (
                <ul className="space-y-1.5">
                  {report.movements.shiftedGantt.map((t) => (
                    <li key={`${t.projectId}/${t.taskId}`} className="rounded-lg border border-border/50 bg-background/50 px-2.5 py-2 text-sm">
                      <span className="break-words font-medium">{t.title}</span>
                      <span className="ml-1 text-xs text-muted-foreground">· {t.projectTitle}</span>
                      <span className="ml-1 text-xs text-muted-foreground tabular-nums">{fmtDateShort(t.oldEnd)} → <span className="font-semibold text-foreground">{fmtDateShort(t.newEnd)}</span></span>
                    </li>
                  ))}
                </ul>
              )}
            </Collapsible>

            <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              {(["customer", "office"] as const).map((sideKey) => {
                const side = report.bySide[sideKey];
                const label = sideKey === "customer" ? "Сторона ДИАК ЗФ (заказчик)" : "Проектный офис (исполнители)";
                return (
                  <Collapsible key={sideKey} title={label} count={side.completedInPeriod.length + side.inProgressNow.length}>
                    <div className="space-y-3">
                      <div>
                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Сделано за период ({side.completedInPeriod.length})</div>
                        <TaskList items={side.completedInPeriod} empty="Ничего за период." />
                      </div>
                      <div>
                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--nn-blue)]">В работе ({side.inProgressNow.length})</div>
                        <TaskList items={side.inProgressNow} empty="Нет активных задач." />
                      </div>
                    </div>
                  </Collapsible>
                );
              })}
            </div>

            {/* ─── По исполнителям ─── */}
            <Collapsible title="По исполнителям" count={report.byPerson.length}>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {report.byPerson.map((p) => (
                  <div key={p.assignee} className="rounded-lg border border-border/60 bg-background/50 p-3">
                    <div className="mb-2 flex items-baseline justify-between">
                      <span className="text-sm font-semibold">{p.assignee}</span>
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">активных: {p.workloadActive}</span>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between text-emerald-700"><span>Завершил за период</span><span className="font-semibold tabular-nums">{p.completedInPeriod.length}</span></div>
                      <div className="flex justify-between text-[var(--nn-blue)]"><span>В работе</span><span className="font-semibold tabular-nums">{p.inProgressNow.length}</span></div>
                      <div className="flex justify-between text-muted-foreground"><span>Новых взял</span><span className="font-semibold tabular-nums">{p.newInPeriod.length}</span></div>
                      {p.overdueNow.length > 0 ? <div className="flex justify-between text-red-700"><span>Просрочено</span><span className="font-semibold tabular-nums">{p.overdueNow.length}</span></div> : null}
                    </div>
                  </div>
                ))}
              </div>
            </Collapsible>

            {/* ─── По проектам ─── */}
            <Collapsible title="Прогресс по проектам" count={report.byProject.length}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="py-2 pr-2">Проект</th>
                      <th className="px-2 py-2">Этап</th>
                      <th className="px-2 py-2 text-right">Было</th>
                      <th className="px-2 py-2 text-right">Стало</th>
                      <th className="px-2 py-2 text-right">Δ</th>
                      <th className="px-2 py-2 text-right">В работе</th>
                      <th className="px-2 py-2 text-right">Готово</th>
                      <th className="pl-2 py-2 text-right">Просроч.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.byProject.map((p) => (
                      <tr key={p.projectId} className="border-b border-border/40 last:border-b-0">
                        <td className="py-1.5 pr-2 font-medium">{p.projectTitle}</td>
                        <td className="px-2 py-1.5 text-muted-foreground">{p.phaseLabel ?? "—"}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{p.progressBefore}%</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{p.progressAfter}%</td>
                        <td className={cn("px-2 py-1.5 text-right tabular-nums font-medium", p.progressDelta > 0 && "text-emerald-600", p.progressDelta < 0 && "text-red-600", p.progressDelta === 0 && "text-muted-foreground")}>
                          {p.progressDelta > 0 ? "+" : ""}{p.progressDelta}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{p.inProgressTotal}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700">{p.doneTotal}</td>
                        <td className="pl-2 py-1.5 text-right tabular-nums text-red-700">{p.overdueTotal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Collapsible>
          </div>
        ) : null}
      </main>
    </div>
  );
}

interface QItem { question: string; options: string[]; }
type Phase = "idle" | "loadingQuestions" | "answering" | "generating" | "done" | "error";

/**
 * Сводка для руководства через интервью: ИИ задаёт уточняющие вопросы по
 * спорным местам → руководитель отвечает (выбор/свой вариант) → ИИ формирует
 * сводку с учётом ответов как приоритетного источника.
 */
function ExecSummary({
  fromPointId,
  toPointId,
  onSaved,
}: {
  fromPointId: string | null;
  toPointId: string | null;
  onSaved?: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [questions, setQuestions] = useState<QItem[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [narrative, setNarrative] = useState<Narrative | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [lastQa, setLastQa] = useState<Array<{ question: string; answer: string }>>([]);
  const [savingReport, setSavingReport] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string>("");

  const loadQuestions = async () => {
    setPhase("loadingQuestions");
    setErrorMsg("");
    try {
      const res = await apiJson<{ ok: true; questions: QItem[] }>("/api/reports/questions", {
        method: "POST",
        body: JSON.stringify({ fromPoint: fromPointId, toPoint: toPointId }),
      });
      if (!res.questions || res.questions.length === 0) {
        // Спорных мест нет — сразу к сводке без вопросов.
        await generate([]);
        return;
      }
      setQuestions(res.questions);
      setAnswers({});
      setPhase("answering");
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Не удалось подготовить вопросы");
      setPhase("error");
    }
  };

  const generate = async (qa: Array<{ question: string; answer: string }>) => {
    setPhase("generating");
    setErrorMsg("");
    setLastQa(qa);
    setSavedMsg("");
    try {
      const res = await apiJson<{ ok: true; narrative: Narrative }>("/api/reports/narrative", {
        method: "POST",
        body: JSON.stringify({ fromPoint: fromPointId, toPoint: toPointId, answers: qa }),
      });
      setNarrative(res.narrative);
      setPhase("done");
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Не удалось сформировать сводку");
      setPhase("error");
    }
  };

  const submitAnswers = () => {
    const qa = questions
      .map((q, i) => ({ question: q.question, answer: (answers[i] ?? "").trim() }))
      .filter((x) => x.answer);
    void generate(qa);
  };

  // Зафиксировать текущий сгенерированный отчёт (цифры + сводка + ответы) в архив.
  const saveReport = async () => {
    if (!narrative) return;
    setSavingReport(true);
    setSavedMsg("");
    try {
      await apiJson("/api/reports/saved", {
        method: "POST",
        body: JSON.stringify({ fromPoint: fromPointId, toPoint: toPointId, narrative, answers: lastQa }),
      });
      setSavedMsg("Отчёт сохранён в архив");
      onSaved?.();
    } catch (e) {
      setSavedMsg(e instanceof Error ? e.message : "Не удалось сохранить");
    } finally {
      setSavingReport(false);
    }
  };

  const status = narrative ? STATUS_META[narrative.status] : STATUS_META.amber;

  return (
    <section className="overflow-hidden rounded-xl border shadow-sm" style={{ borderColor: phase === "done" ? status.ring : "var(--border)" }}>
      <div className="p-5" style={{ background: phase === "done" ? status.bg : "rgba(0,85,165,0.04)" }}>
        <div className="mb-2 flex items-center gap-2">
          <Sparkles size={14} className="text-[var(--nn-blue)]" />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Сводка для руководства</span>
          {phase === "done" || phase === "answering" ? (
            <button type="button" onClick={() => void loadQuestions()} className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-black/5 print:hidden">
              <RefreshCw size={12} /> заново
            </button>
          ) : null}
        </div>

        {/* IDLE */}
        {phase === "idle" ? (
          <div className="flex flex-col items-start gap-2">
            <p className="text-sm text-muted-foreground">
              ИИ задаст несколько уточняющих вопросов по спорным местам (отстающие/не начатые проекты, риски), а затем сформирует сводку с учётом ваших ответов. Ваши ответы важнее автоматических выводов.
            </p>
            <button type="button" onClick={() => void loadQuestions()}
              className="inline-flex items-center gap-1.5 rounded-md bg-[var(--nn-blue)] px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-[var(--nn-blue-dark)]">
              <Sparkles size={14} /> Подготовить сводку
            </button>
          </div>
        ) : null}

        {/* LOADING QUESTIONS */}
        {phase === "loadingQuestions" ? (
          <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> ИИ готовит уточняющие вопросы по данным отчёта…
          </div>
        ) : null}

        {/* ANSWERING */}
        {phase === "answering" ? (
          <div className="space-y-4">
            <p className="text-[12px] text-muted-foreground">Ответьте на вопросы (или выберите «свой вариант»). Это уточнит сводку и скорректирует авто-выводы.</p>
            {questions.map((q, i) => {
              const sel = answers[i] ?? "";
              const isCustom = sel !== "" && !q.options.includes(sel);
              return (
                <div key={i} className="rounded-lg border border-border/60 bg-card p-3">
                  <div className="mb-2 text-sm font-medium" style={{ color: "var(--nn-dark)" }}>{i + 1}. {q.question}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {q.options.map((opt) => (
                      <button key={opt} type="button" onClick={() => setAnswers((a) => ({ ...a, [i]: opt }))}
                        className={cn("rounded-full border px-2.5 py-1 text-[12px] transition-colors",
                          sel === opt ? "border-[var(--nn-blue)] bg-[var(--nn-blue)] text-white" : "border-border bg-background text-foreground/80 hover:border-[var(--nn-blue)]/50")}>
                        {opt}
                      </button>
                    ))}
                    <button type="button" onClick={() => setAnswers((a) => ({ ...a, [i]: isCustom ? sel : " " }))}
                      className={cn("rounded-full border px-2.5 py-1 text-[12px]", isCustom ? "border-[var(--nn-blue)] bg-[var(--nn-blue)] text-white" : "border-dashed border-border text-muted-foreground hover:border-[var(--nn-blue)]/50")}>
                      свой вариант
                    </button>
                  </div>
                  {isCustom ? (
                    <input type="text" autoFocus value={sel.trim()} onChange={(e) => setAnswers((a) => ({ ...a, [i]: e.target.value || " " }))}
                      placeholder="Впишите свой ответ…"
                      className="mt-2 w-full rounded-md border border-border bg-background px-2 py-1 text-sm" />
                  ) : null}
                </div>
              );
            })}
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={submitAnswers}
                className="inline-flex items-center gap-1.5 rounded-md bg-[var(--nn-blue)] px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-[var(--nn-blue-dark)]">
                <Sparkles size={14} /> Сформировать сводку
              </button>
              <button type="button" onClick={() => void generate([])}
                className="rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-muted/50">
                Пропустить вопросы
              </button>
            </div>
          </div>
        ) : null}

        {/* GENERATING */}
        {phase === "generating" ? (
          <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Формирую сводку с учётом ваших ответов…
          </div>
        ) : null}

        {/* ERROR */}
        {phase === "error" ? (
          <div className="py-2 text-sm text-muted-foreground">
            {errorMsg || "Не удалось сформировать сводку."}{" "}
            <button onClick={() => void loadQuestions()} className="text-[var(--nn-blue)] underline print:hidden">Повторить</button>
          </div>
        ) : null}

        {/* DONE */}
        {phase === "done" && narrative ? (
          <div className="space-y-3">
            <NarrativeCard narrative={narrative} />
            <div className="flex flex-wrap items-center gap-2 print:hidden">
              <button type="button" onClick={() => void saveReport()} disabled={savingReport}
                className="inline-flex items-center gap-1.5 rounded-md border border-[var(--nn-blue)]/40 bg-[var(--nn-blue-pale)]/40 px-3 py-1.5 text-sm font-medium text-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)]/70 disabled:opacity-60">
                {savingReport ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={14} />}
                Зафиксировать отчёт в архив
              </button>
              {savedMsg ? <span className="text-xs text-green-700">{savedMsg}</span> : null}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/* ───────────────────── Карточка ИИ-сводки (живой и сохранённой) ───────────────────── */
function NarrativeCard({ narrative }: { narrative: Narrative }) {
  const status = STATUS_META[narrative.status] ?? STATUS_META.amber;
  return (
    <div className="flex items-start gap-4">
      <div className="flex shrink-0 flex-col items-center gap-1">
        <span className="flex h-12 w-12 items-center justify-center rounded-full" style={{ background: status.color }}>
          <span className="h-5 w-5 rounded-full bg-white/90" />
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: status.color }}>{status.label}</span>
      </div>
      <div className="min-w-0 flex-1">
        {narrative.headline ? <h2 className="text-lg font-bold leading-snug" style={{ color: "var(--nn-dark)" }}>{narrative.headline}</h2> : null}
        {narrative.statusReason ? <p className="mt-0.5 text-[12px]" style={{ color: status.color }}>{narrative.statusReason}</p> : null}
        {narrative.summary ? <p className="mt-2 text-sm leading-relaxed text-foreground/90">{narrative.summary}</p> : null}
        {narrative.attention.length > 0 ? (
          <div className="mt-3">
            <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-red-700">
              <Megaphone size={12} /> Что требует решения руководства
            </div>
            <ul className="ml-1 space-y-1">
              {narrative.attention.map((a, i) => (
                <li key={i} className="flex gap-1.5 text-sm text-foreground/90">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: status.color }} />
                  <span className="break-words">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <p className="mt-3 text-[10px] text-muted-foreground/60">
          Сводка сформирована ИИ на основе данных дашборда и ответов на уточняющие вопросы. Цифры в карточках ниже — первоисточник.
        </p>
      </div>
    </div>
  );
}

/* ───────────────────── Архив сохранённых отчётов ───────────────────── */
function ReportArchivePanel({
  saved,
  onOpen,
  onDelete,
}: {
  saved: LightSaved[];
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  if (saved.length === 0) return null;
  return (
    <section className="border-b border-border bg-muted/10 px-6 py-3 print:hidden">
      <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <Archive size={12} className="text-[var(--nn-blue)]" /> Сохранённые отчёты ({saved.length})
      </div>
      <div className="flex flex-col gap-1.5">
        {saved.map((r) => {
          const dot =
            r.status === "green" ? "#16a34a" : r.status === "amber" ? "#d97706" : r.status === "red" ? "#e11d48" : "#94a3b8";
          return (
            <div key={r.id} className="group flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-sm">
              <button
                type="button"
                onClick={() => onOpen(r.id)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left hover:text-[var(--nn-blue)]"
                title="Открыть сохранённый отчёт"
              >
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: dot }} aria-hidden />
                <FileText size={14} className="shrink-0 text-muted-foreground" />
                <span className="truncate font-medium">{r.title}</span>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  · {fmtDateShort(r.fromDate)} → {fmtDateShort(r.toDate)} · сохранён {fmtDateShort(r.savedAt)}
                  {r.authorName ? ` · ${r.authorName}` : ""}
                </span>
              </button>
              <button
                type="button"
                onClick={() => onDelete(r.id)}
                title="Удалить из архива"
                className="shrink-0 rounded p-1 text-muted-foreground/50 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
              >
                <Trash2 size={13} />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ───────────────────── Точки отчёта: панель + пустое состояние ───────────────────── */
function ReportPointsPanel({
  points,
  fromPointId,
  toPointId,
  onOpen,
  onDelete,
}: {
  points: LightPoint[];
  fromPointId: string | null;
  toPointId: string | null;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  if (points.length === 0) return null;
  return (
    <section className="border-b border-border bg-muted/20 px-6 py-3 print:hidden">
      <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <Flag size={12} className="text-[var(--nn-blue)]" /> Точки отчёта ({points.length})
      </div>
      <div className="flex flex-wrap gap-2">
        {points.map((p, i) => {
          const isBaseline = i === 0;
          const isFrom = p.id === fromPointId;
          const isTo = p.id === toPointId;
          const inPair = isFrom || isTo;
          return (
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => onOpen(p.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onOpen(p.id);
                }
              }}
              title="Открыть отчёт по этой точке"
              className={cn(
                "group relative flex min-w-[160px] cursor-pointer flex-col gap-0.5 rounded-lg border px-3 py-2 text-xs transition-colors hover:border-[var(--nn-blue)]/70 hover:shadow-sm",
                inPair ? "border-[var(--nn-blue)] bg-[var(--nn-blue-pale)]/40" : "border-border bg-background",
              )}
            >
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-foreground">{p.label}</span>
                {isBaseline ? (
                  <span className="rounded bg-[var(--nn-blue)]/10 px-1 text-[9px] font-medium text-[var(--nn-blue)]">baseline</span>
                ) : null}
                {isFrom ? <span className="rounded bg-muted px-1 text-[9px] text-muted-foreground">от</span> : null}
                {isTo ? <span className="rounded bg-muted px-1 text-[9px] text-muted-foreground">до</span> : null}
              </div>
              <span className="text-muted-foreground">{fmtDate(p.capturedAt)}</span>
              <span className="text-[10px] text-muted-foreground/80">
                прогресс {p.averageProgress}% · проектов {p.projectsCount}
                {p.authorName ? ` · ${p.authorName}` : ""}
              </span>
              {p.note ? <span className="text-[10px] italic text-muted-foreground/70">{p.note}</span> : null}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDelete(p.id); }}
                title="Удалить точку"
                className="absolute right-1 top-1 rounded p-0.5 text-muted-foreground/50 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
              >
                <Trash2 size={12} />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function EmptyHint({
  title,
  text,
  cta,
  onCapture,
  capturing,
}: {
  title: string;
  text: string;
  cta: string;
  onCapture: () => void;
  capturing: boolean;
}) {
  return (
    <div className="m-6 flex flex-col items-center gap-3 rounded-xl border border-dashed border-border bg-card/40 p-10 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--nn-blue-pale)]/60 text-[var(--nn-blue)]">
        <Flag size={20} />
      </span>
      <h2 className="text-base font-semibold" style={{ color: "var(--nn-dark)" }}>{title}</h2>
      <p className="max-w-md text-sm text-muted-foreground">{text}</p>
      <button
        type="button"
        onClick={onCapture}
        disabled={capturing}
        className="inline-flex items-center gap-1.5 rounded-md bg-[var(--nn-blue)] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[var(--nn-blue-dark)] disabled:opacity-60"
      >
        {capturing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flag size={14} />} {cta}
      </button>
    </div>
  );
}
